<template>
  <div class="crew-calculator-wrapper">
    <!-- Crew Calculator -->
    <div class="crew-calculator-section">
      <h5>Install Crew Calculator</h5>
      <div class="calculator-controls">
        <div class="control-group">
          <label for="crew-size">Crew Size:</label>
          <select id="crew-size" v-model.number="crewSize" class="form-control">
            <option v-for="n in 10" :key="n" :value="n">{{ n }}</option>
          </select>
        </div>
        <div class="control-group">
          <label for="total-labor-hours">Total Labor Hours:</label>
          <input
            id="total-labor-hours"
            type="number"
            v-model.number="totalLaborHours"
            class="form-control"
            min="1"
            step="1"
          />
        </div>
      </div>

      <div class="calculator-results">
        <div class="result-card primary-result">
          <div class="result-label">Rate per Crew Member</div>
          <div class="result-value">{{ hourlyRatePerMember | currencyFilter }}/hr</div>
          <div class="result-breakdown">
            <span>{{ dailyRatePerMember | currencyFilter }}/day</span>
            <span class="separator">•</span>
            <span>{{ totalPerCrewMember | currencyFilter }} total</span>
          </div>
        </div>
        <div class="result-card">
          <div class="result-label">Total Days to Complete</div>
          <div class="result-value">{{ totalDays }} {{ totalDays === 1 ? 'day' : 'days' }}</div>
          <div class="result-note">({{ hoursPerDay }} hours/day)</div>
        </div>
        <div class="result-card comparison-card" :class="{ positive: rateVsTarget >= 0, negative: rateVsTarget < 0 }">
          <div class="result-label">vs. Target $25/hr</div>
          <div class="result-value">{{ rateVsTarget >= 0 ? '+' : '' }}{{ rateVsTarget | currencyFilter }}/hr</div>
          <div class="result-note">{{ rateVsTarget >= 0 ? 'Above' : 'Below' }} target</div>
        </div>
      </div>
    </div>

    <!-- Bonus Distribution Calculator -->
    <div class="bonus-distribution-section">
      <h5>Bonus Distribution (Above $25/hr Target)</h5>
      <div class="bonus-info-bar">
        <div class="bonus-pool-display">
          <span class="label">Total Bonus Pool:</span>
          <span class="value">{{ totalBonusPool | currencyFilter }}</span>
        </div>
        <div class="bonus-per-hour">
          <span class="label">Bonus per Hour:</span>
          <span class="value">+{{ bonusPerHour | currencyFilter }}/hr</span>
        </div>
      </div>

      <div class="crew-distribution-table">
        <div class="table-header">
          <div class="col-member">Member</div>
          <div class="col-rate">Base Rate</div>
          <div class="col-base-amount">Base Amount</div>
          <div class="col-percentage">Bonus %</div>
          <div class="col-bonus">Bonus Amount</div>
          <div class="col-total">Total Earnings</div>
        </div>

        <div 
          v-for="(member, index) in crewMembers" 
          :key="index"
          class="table-row"
        >
          <div class="col-member">
            <strong>Crew {{ index + 1 }}</strong>
          </div>
          <div class="col-rate">
            <input
              type="number"
              v-model.number="member.baseRate"
              class="rate-input"
              min="0"
              step="0.5"
            />
            <span class="unit">/hr</span>
          </div>
          <div class="col-base-amount">
            <span class="base-amount-value">{{ getMemberBaseAmount(member) | currencyFilter }}</span>
          </div>
          <div class="col-percentage">
            <input
              type="number"
              v-model.number="member.bonusPercentage"
              class="percentage-input"
              min="0"
              max="100"
              step="1"
            />
            <span class="unit">%</span>
          </div>
          <div class="col-bonus">
            <span class="bonus-value">{{ getMemberBonus(member) | currencyFilter }}</span>
          </div>
          <div class="col-total">
            <span class="total-value">{{ getMemberTotal(member) | currencyFilter }}</span>
          </div>
        </div>

        <div class="table-footer">
          <div class="col-member"></div>
          <div class="col-rate"></div>
          <div class="col-base-amount">
            <strong>{{ totalBaseAmount | currencyFilter }}</strong>
          </div>
          <div class="col-percentage">
            <strong>{{ totalBonusPercentage }}%</strong>
          </div>
          <div class="col-bonus">
            <strong>{{ totalDistributedBonus | currencyFilter }}</strong>
          </div>
          <div class="col-total">
            <strong>{{ totalCrewEarnings | currencyFilter }}</strong>
          </div>
        </div>
      </div>

      <button 
        @click="resetBonusDistribution" 
        class="btn btn-sm btn-outline-secondary mt-3"
      >
        Reset to Equal Distribution
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrewCalculator',
  props: {
    jobInstallRate: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      crewSize: 4,
      totalLaborHours: 0,
      hoursPerDay: 8,
      targetHourlyRate: 25,
      crewMembers: [],
    }
  },
  watch: {
    crewSize() {
      this.initializeCrewMembers()
    },
    jobInstallRate: {
      immediate: true,
      handler(newVal) {
        if (newVal > 0 && this.totalLaborHours === 0) {
          this.totalLaborHours = Math.ceil(newVal / this.targetHourlyRate)
          this.initializeCrewMembers()
        }
      },
    },
  },
  computed: {
    hourlyRatePerMember() {
      if (this.totalLaborHours === 0 || this.crewSize === 0) return 0
      return this.jobInstallRate / this.totalLaborHours
    },
    hoursPerCrewMember() {
      if (this.crewSize === 0) return 0
      return this.totalLaborHours / this.crewSize
    },
    totalDays() {
      if (this.hoursPerCrewMember === 0 || this.hoursPerDay === 0) return 0
      return Math.ceil(this.hoursPerCrewMember / this.hoursPerDay)
    },
    rateVsTarget() {
      return this.hourlyRatePerMember - this.targetHourlyRate
    },
    totalPerCrewMember() {
      if (this.crewSize === 0) return 0
      return this.jobInstallRate / this.crewSize
    },
    dailyRatePerMember() {
      return this.hourlyRatePerMember * this.hoursPerDay
    },
    totalBaseAmount() {
      return this.crewMembers.reduce((sum, member) => sum + this.getMemberBaseAmount(member), 0)
    },
    totalBonusPool() {
      return Math.max(0, this.jobInstallRate - this.totalBaseAmount)
    },
    bonusPerHour() {
      if (this.totalLaborHours === 0) return 0
      return this.totalBonusPool / this.totalLaborHours
    },
    totalBonusPercentage() {
      return this.crewMembers.reduce((sum, member) => sum + (member.bonusPercentage || 0), 0)
    },
    totalDistributedBonus() {
      return this.crewMembers.reduce((sum, member) => sum + this.getMemberBonus(member), 0)
    },
    totalCrewEarnings() {
      return this.crewMembers.reduce((sum, member) => sum + this.getMemberTotal(member), 0)
    },
  },
  methods: {
    initializeCrewMembers() {
      const equalPercentage = Math.floor(100 / this.crewSize)
      const remainder = 100 - (equalPercentage * this.crewSize)
      
      this.crewMembers = []
      for (let i = 0; i < this.crewSize; i++) {
        this.crewMembers.push({
          baseRate: this.targetHourlyRate,
          bonusPercentage: i === 0 ? equalPercentage + remainder : equalPercentage,
        })
      }
    },
    resetBonusDistribution() {
      this.initializeCrewMembers()
    },
    getMemberBaseAmount(member) {
      const hoursPerMember = this.totalLaborHours / this.crewSize
      return (member.baseRate || this.targetHourlyRate) * hoursPerMember
    },
    getMemberBonus(member) {
      if (!this.totalBonusPool) return 0
      return (this.totalBonusPool * (member.bonusPercentage || 0)) / 100
    },
    getMemberTotal(member) {
      return this.getMemberBaseAmount(member) + this.getMemberBonus(member)
    },
  },
}
</script>

<style lang="scss" scoped>
.crew-calculator-wrapper {
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.crew-calculator-section {
  h5 {
    color: #495057;
    margin-bottom: 20px;
    font-size: 18px;
  }
}

.calculator-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 25px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.control-group {
  display: flex;
  flex-direction: column;

  label {
    font-weight: 600;
    margin-bottom: 8px;
    color: #495057;
    font-size: 14px;
  }

  .form-control {
    padding: 10px 15px;
    font-size: 16px;
    border: 2px solid #dee2e6;
    border-radius: 6px;
    transition: border-color 0.2s;

    &:focus {
      border-color: #007bff;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
      outline: none;
    }
  }

  select.form-control {
    cursor: pointer;
    padding-top: 6px;
    padding-right: 35px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
  }
}

.calculator-results {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.result-card {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &.primary-result {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;

    .result-label {
      color: rgba(255, 255, 255, 0.9);
    }

    .result-value {
      color: white;
      font-size: 32px;
    }

    .result-breakdown {
      color: rgba(255, 255, 255, 0.95);
      font-size: 16px;
      font-weight: 600;
      margin-top: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      .separator {
        opacity: 0.5;
        font-size: 14px;
      }
    }
  }

  &.comparison-card {
    &.positive {
      background: linear-gradient(135deg, #56ab2f 0%, #a8e063 100%);
      color: white;
      border: none;

      .result-label, .result-value, .result-note {
        color: white;
      }

      .result-note {
        opacity: 0.9;
      }
    }

    &.negative {
      background: linear-gradient(135deg, #eb3349 0%, #f45c43 100%);
      color: white;
      border: none;

      .result-label, .result-value, .result-note {
        color: white;
      }

      .result-note {
        opacity: 0.9;
      }
    }
  }
}

.result-label {
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.result-value {
  font-size: 28px;
  font-weight: 700;
  color: #212529;
  margin-bottom: 5px;
}

.result-note {
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
  margin-top: 5px;
}

.bonus-distribution-section {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 3px solid #dee2e6;

  h5 {
    color: #495057;
    margin-bottom: 20px;
    font-size: 18px;
  }
}

.bonus-info-bar {
  display: flex;
  justify-content: space-around;
  gap: 20px;
  margin-bottom: 25px;
  padding: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;

  .bonus-pool-display,
  .bonus-per-hour {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;

    .label {
      font-size: 13px;
      opacity: 0.9;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
    }
  }
}

.crew-distribution-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .table-header,
  .table-row,
  .table-footer {
    display: grid;
    grid-template-columns: 100px 140px 130px 120px 130px 140px;
    gap: 10px;
    align-items: center;
    padding: 12px 15px;
  }

  .table-header {
    background: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    font-size: 13px;
    color: #495057;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .table-row {
    border-bottom: 1px solid #e9ecef;
    transition: background-color 0.2s;

    &:hover {
      background: #f8f9fa;
    }

    &:last-of-type {
      border-bottom: 2px solid #dee2e6;
    }
  }

  .table-footer {
    background: #f8f9fa;
    font-weight: 600;
    color: #495057;
  }

  .col-member {
    strong {
      color: #667eea;
      font-size: 15px;
    }
  }

  .col-rate,
  .col-percentage {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .rate-input,
  .percentage-input {
    width: 80px;
    padding: 6px 10px;
    border: 2px solid #dee2e6;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
  }

  .unit {
    font-size: 13px;
    color: #6c757d;
    font-weight: 500;
  }

  .base-amount-value {
    color: #6c757d;
    font-weight: 600;
    font-size: 15px;
  }

  .bonus-value {
    color: #28a745;
    font-weight: 600;
    font-size: 15px;
  }

  .total-value {
    color: #495057;
    font-weight: 700;
    font-size: 16px;
  }
}
</style>
